import { useState } from 'react';

export default function QuoteForm({ quoteSubmitted, setQuoteSubmitted, isLoading, setIsLoading }) {
  const [quoteData, setQuoteData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: ''
  });

  const handleQuoteChange = (e) => {
    const { name, value } = e.target;
    setQuoteData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/email/get-a-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit quote');
      }

      setQuoteSubmitted(true);
      setQuoteData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        budget: '',
        timeline: '',
        description: ''
      });

    } catch (error) {
      console.error('Error submitting quote:', error);
      alert('Failed to submit quote. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (quoteSubmitted) {
    return (
      <div className="bg-white rounded-lg shadow-md p-10 text-center">
        <div className="text-green-500 text-5xl mb-6">
          <i className="fas fa-check-circle"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-4">Quote Request Received!</h3>
        <p className="text-gray-600 mb-6">Thank you for your interest in DevsKe. We're reviewing your project requirements and will send you a detailed quote within 24 hours.</p>
        <button 
          onClick={() => setQuoteSubmitted(false)} 
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium transition duration-300"
          aria-label="Request another quote"
        >
          Request Another Quote
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-8 md:p-10">
      <form onSubmit={handleQuoteSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <label htmlFor="quote-name" className="block text-gray-700 font-medium mb-2">
              Your Name <span className="text-red-500">*</span>
            </label>
            <input 
              type="text" 
              id="quote-name" 
              name="name" 
              value={quoteData.name}
              onChange={handleQuoteChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required 
              aria-required="true"
            />
          </div>
          
          <div>
            <label htmlFor="quote-email" className="block text-gray-700 font-medium mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input 
              type="email" 
              id="quote-email" 
              name="email" 
              value={quoteData.email}
              onChange={handleQuoteChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required
              aria-required="true"
            />
          </div>
          
          <div>
            <label htmlFor="quote-phone" className="block text-gray-700 font-medium mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input 
              type="tel" 
              id="quote-phone" 
              name="phone" 
              value={quoteData.phone}
              onChange={handleQuoteChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required
              aria-required="true"
            />
          </div>
          
          <div>
            <label htmlFor="quote-projectType" className="block text-gray-700 font-medium mb-2">
              Project Type <span className="text-red-500">*</span>
            </label>
            <select 
              id="quote-projectType" 
              name="projectType" 
              value={quoteData.projectType}
              onChange={handleQuoteChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required
              aria-required="true"
            >
              <option value="">Select project type</option>
              <option value="web-development">Web Development</option>
              <option value="mobile-app">Mobile App Development</option>
              <option value="cloud-solution">Cloud Solution</option>
              <option value="real-estate">Real Estate System</option>
              <option value="sports-book">Sports Book Platform</option>
              <option value="custom-software">Custom Software</option>
              <option value="e-commerce">E-Commerce Systems</option>
              <option value="wordpress">WordPress Services</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="quote-budget" className="block text-gray-700 font-medium mb-2">
              Estimated Budget (USD) <span className="text-red-500">*</span>
            </label>
            <select 
              id="quote-budget" 
              name="budget" 
              value={quoteData.budget}
              onChange={handleQuoteChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required
              aria-required="true"
            >
              <option value="">Select budget range</option>
              <option value="500-1k">$500 - $1,000</option>
              <option value="1k-2.5k">$1,000 - $2,500</option>
              <option value="2.5k-5k">$2,500 - $5,000</option>
              <option value="5k-10k">$5,000 - $10,000</option>
              <option value="10k-20k">$10,000 - $20,000</option>
              <option value="20k-30k">$20,000 - $30,000</option>
              <option value="30k-50k">$30,000 - $50,000</option>
              <option value="50k+">$50,000+</option>
              <option value="not-sure">Not Sure</option>
            </select>
          </div>
          
          <div>
            <label htmlFor="quote-timeline" className="block text-gray-700 font-medium mb-2">
              Project Timeline <span className="text-red-500">*</span>
            </label>
            <select 
              id="quote-timeline" 
              name="timeline" 
              value={quoteData.timeline}
              onChange={handleQuoteChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
              required
              aria-required="true"
            >
              <option value="">Select timeline</option>
              <option value="1-2months">1-2 Months</option>
              <option value="2-4months">2-4 Months</option>
              <option value="4-6months">4-6 Months</option>
              <option value="6months+">6+ Months</option>
              <option value="not-sure">Not Sure</option>
            </select>
          </div>
        </div>
        
        <div className="mb-8">
          <label htmlFor="quote-description" className="block text-gray-700 font-medium mb-2">
            Project Description <span className="text-red-500">*</span>
          </label>
          <textarea 
            id="quote-description" 
            name="description" 
            rows="6" 
            value={quoteData.description}
            onChange={handleQuoteChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
            placeholder="Describe your project in detail, including features, functionalities, and any specific requirements..."
            required
            aria-required="true"
          ></textarea>
        </div>
        
        <div className="mb-6">
          <label className="flex items-start">
            <input 
              type="checkbox" 
              className="mt-1 mr-2" 
              required 
              aria-required="true"
            />
            <span className="text-gray-700">
              I agree to DevsKe's{' '}
              <a 
                href="/terms-of-service" 
                className="text-blue-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a 
                href="/privacy-policy" 
                className="text-blue-600 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              <span className="text-red-500">*</span>
            </span>
          </label>
        </div>
        
        <button 
          type="submit" 
          className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-md font-medium transition duration-300 flex justify-center items-center"
          disabled={isLoading}
          aria-label={isLoading ? "Processing your quote" : "Submit quote request"}
        >
          {isLoading ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i> Processing...
            </>
          ) : (
            'Get Your Free Quote'
          )}
        </button>
      </form>
    </div>
  );
}